/**
 * Arcℏimedian DSaaS — Cookie Consent Banner
 * LGPD-compliant: distinguishes essential vs. analytical cookies.
 * Stores preference in localStorage under 'archimedian_cookie_consent'.
 */

import React, { useEffect, useState } from 'react';
import { theme as T } from '../theme';

type ConsentValue = 'all' | 'essential' | null;

const STORAGE_KEY = 'archimedian_cookie_consent';

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) setVisible(true);
  }, []);

  const accept = (value: ConsentValue) => {
    if (value) {
      localStorage.setItem(STORAGE_KEY, value);
      localStorage.setItem(`${STORAGE_KEY}_date`, new Date().toISOString());
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Preferências de cookies"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        width: 'min(600px, calc(100vw - 32px))',
        background: T.bgCard,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
        padding: 24,
        animation: 'slideUp 0.3s ease',
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
        <span style={{ fontSize: 28, flexShrink: 0, marginTop: 2 }}>🍪</span>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 6 }}>
            Usamos cookies
          </div>
          <p style={{ fontSize: 13, color: T.textDim, lineHeight: 1.6, margin: 0 }}>
            Utilizamos cookies essenciais para o funcionamento da plataforma e, com sua autorização,
            cookies analíticos para melhorar sua experiência. Confira nossa{' '}
            <a href="/privacy" style={{ color: T.accent }}>Política de Privacidade</a>{' '}
            para mais detalhes.
          </p>
        </div>
      </div>

      {expanded && (
        <div style={{
          marginBottom: 16, padding: 16,
          background: T.surface, borderRadius: T.radiusSm,
          fontSize: 13, color: T.textDim,
        }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <strong style={{ color: T.text }}>Cookies essenciais</strong>
              <span style={{ color: T.textDim, fontSize: 11 }}>Sempre ativos</span>
            </div>
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              Necessários para autenticação, segurança e funcionamento básico da plataforma. Não podem ser desativados.
            </p>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <strong style={{ color: T.text }}>Cookies analíticos</strong>
              <span style={{ color: T.accent, fontSize: 11 }}>Requer consentimento</span>
            </div>
            <p style={{ margin: 0, lineHeight: 1.6 }}>
              Coletam dados anônimos sobre como você usa a plataforma para que possamos melhorá-la. Nenhum dado pessoal identificável é compartilhado.
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={() => accept('all')}
          style={{
            padding: '9px 20px',
            background: T.accent, color: '#fff',
            border: 'none', borderRadius: T.radiusSm,
            fontWeight: 600, fontSize: 13, cursor: 'pointer',
            flex: '1 0 auto',
          }}
        >
          Aceitar todos
        </button>
        <button
          onClick={() => accept('essential')}
          style={{
            padding: '9px 20px',
            background: 'none',
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm,
            color: T.textMuted, fontSize: 13, cursor: 'pointer',
            flex: '1 0 auto',
          }}
        >
          Somente essenciais
        </button>
        <button
          onClick={() => setExpanded(e => !e)}
          style={{
            padding: '9px 14px',
            background: 'none', border: 'none',
            color: T.textDim, fontSize: 12, cursor: 'pointer',
          }}
        >
          {expanded ? 'Ocultar detalhes' : 'Personalizar'}
        </button>
      </div>
    </div>
  );
}

export function getCookieConsent(): ConsentValue {
  try {
    return (localStorage.getItem(STORAGE_KEY) as ConsentValue) ?? null;
  } catch {
    return null;
  }
}
